import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AGENCY_KPI_DEFS,
  AGENCY_KPI_MAP,
  KPI_GRADE_DEFS,
  type AgencyKpiCard,
  type AgencyKpiKey,
  type KpiValue,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { WORDS, num, plural } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Notice, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';

/* ==========================================================================
   فريق العمل — من ينفّذ، وكيف يُقاس
   ========================================================================== */

/**
 * الفريقُ يُبنى من الموارد البشرية لا هنا.
 *
 * الموظّفُ يُنشأ في ملفّه ويُختار قسمُه، فمن كان قسمُه تنفيذيًّا ظهر في هذه
 * القائمة تلقائيًّا. ولا يُضاف أحدٌ من هنا: قائمتان لأسماء الناس تعنيان
 * اسمًا في إحداهما لا يعرفه الآخر.
 *
 * ## ولا مالَ في هذه الشاشة
 *
 * فريقُ المبيعات يُقاس بالهدف والعمولة، وهذا الفريقُ لا يبيع — فالسؤالُ
 * عنه مختلف: هل يُسلّم في موعده، وهل يُقبل ما يُسلّمه من أوّل مرّة، وهل
 * يرضى عنه العميل؟ ثلاثةُ بنودٍ محسوبةٍ من العمل نفسِه لا نسبةٍ تُكتب بيد.
 *
 * ## ولا لوحةَ صدارة
 *
 * الترتيبُ أبجديٌّ ثابت، وليس ذلك سهوًا: عمودٌ يُرتَّب بالدرجة يصنع سباقًا
 * بين زملاءَ مهامُّهم مختلفة، ومصمِّمٌ لا يُقارَن بمطوّر. والمقارنةُ داخل
 * القسم وحدَه، والدرجةُ ترفع الرايةَ ولا تصدر حكمًا — يقرأ الرقمَ إنسانٌ
 * ثمّ يقرّر.
 */

interface KpiTeam {
  windowDays: number;
  from: string;
  cards: AgencyKpiCard[];
}

const WINDOWS = [
  { days: 30, label: 'شهر' },
  { days: 90, label: 'ثلاثة أشهر' },
  { days: 180, label: 'ستة أشهر' },
];

export function AgencyTeam() {
  const navigate = useNavigate();
  const [windowDays, setWindowDays] = useState(90);
  const [q, setQ] = useState('');
  const [explain, setExplain] = useState(false);

  const team = useQuery({
    queryKey: ['agency', 'kpi-team', windowDays],
    queryFn: () => api<KpiTeam>(`/agency/kpi/team${qs({ windowDays })}`),
    placeholderData: (prev) => prev,
  });

  const queue = useQuery({
    queryKey: ['agency', 'kpi-queue'],
    queryFn: () => api<{ id: string }[]>('/agency/kpi/queue'),
  });

  const all = team.data?.cards ?? [];

  const rows = useMemo(() => {
    const term = q.trim();
    if (!term) return all;
    return all.filter(
      (m) =>
        m.name.includes(term) ||
        (m.jobTitle ?? '').includes(term) ||
        (m.deptLabel ?? '').includes(term),
    );
  }, [all, q]);

  const noAccount = all.filter((m) => m.state === 'NO_ACCOUNT');
  const thin = all.filter((m) => m.state === 'THIN_DATA');
  const measured = all.filter((m) => m.score != null);
  const avg = measured.length
    ? Math.round(measured.reduce((s, m) => s + (m.score ?? 0), 0) / measured.length)
    : null;
  const pending = queue.data?.length ?? 0;

  if (team.isError) {
    return (
      <div className="page">
        <ErrorState error={team.error} onRetry={() => void team.refetch()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHead
        title="فريق العمل"
        subtitle="من ينفّذ مهامّ المشاريع، وكيف يُقاس — بلا هدفٍ ماليٍّ ولا عمولة"
        actions={
          <>
            <span className="seg" role="group" aria-label="نافذة القياس">
              {WINDOWS.map((w) => (
                <button
                  key={w.days}
                  type="button"
                  className={`seg-btn${windowDays === w.days ? ' is-on' : ''}`}
                  onClick={() => setWindowDays(w.days)}
                >
                  {w.label}
                </button>
              ))}
            </span>
            <input
              className="input"
              style={{ maxWidth: '13rem' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث باسمٍ أو قسم"
            />
            <button
              className="btn"
              onClick={() => setExplain((v) => !v)}
              title="كيف تُحسب هذه الأرقام"
            >
              <Icon name="info" size={15} />
              كيف تُقاس
            </button>
          </>
        }
      />

      {team.isPending && !team.data && <div className="skeleton" style={{ height: 240 }} />}

      {team.data && (
        <>
          <div className="ag-stats">
            <span className="ag-stat">
              <Icon name="users" size={18} />
              <strong className="num">{all.length}</strong>
              <span>في الفريق</span>
            </span>
            <span className="ag-stat">
              <Icon name="chart" size={18} />
              <strong className="num">{avg == null ? '—' : `${avg}`}</strong>
              <span>متوسّط النقاط</span>
            </span>
            <Link to="/agency/team?review=1" className={`ag-stat${pending > 0 ? ' is-alert' : ''}`}>
              <Icon name="check" size={18} />
              <strong className="num">{pending}</strong>
              <span>تسليمًا ينتظر المراجعة</span>
            </Link>
            <span className="ag-stat">
              <Icon name="alert" size={18} />
              <strong className="num">{thin.length + noAccount.length}</strong>
              <span>بلا قياسٍ بعد</span>
            </span>
          </div>

          {explain && <HowMeasured onClose={() => setExplain(false)} />}

          <div className="notices">
            {noAccount.length > 0 && (
              <Notice
                kind="warning"
                icon="user"
                title={`${plural(noAccount.length, WORDS.member)} بلا حساب مستخدم`}
                action={
                  <Link to="/settings/team" className="btn btn-sm">
                    إدارة الفريق
                  </Link>
                }
              >
                المهمّةُ تُسنَد إلى حساب مستخدمٍ لا إلى ملفّ موظّف — فمن لا حسابَ له لا يصله عمل،
                ولا يُعطى صفرًا: لا يُقاس أصلًا.
                <span className="notice-names">
                  {noAccount.map((m) => (
                    <Link key={m.employeeId} to={`/hr/employees/${m.employeeId}`} className="notice-name">
                      {m.name}
                      <Icon name="chevronLeft" size={12} />
                    </Link>
                  ))}
                </span>
              </Notice>
            )}

            {pending > 0 && (
              <Notice
                kind="info"
                icon="check"
                title={`${num(pending)} تسليمًا ينتظر المراجعة`}
              >
                بندُ «الردّ للتعديل» لا يُحتسب لمن لم تُراجَع تسليماتُه — والنظامُ يختار العيّنة
                (أقدمُ تسليماتِ كلّ منفّذٍ) لا المراجع، وإلّا رُوجع السهلُ وتُرك الصعب. افتح صفحةَ
                أيّ عضوٍ وراجع من هناك.
              </Notice>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">المنفّذون</h2>
              <span className="tiny mute">
                يُضافون من ملفّاتهم في الموارد البشرية باختيار قسمٍ تنفيذيّ
              </span>
            </div>

            <div className="table-wrap sticky-head">
              <table className="table">
                <thead>
                  <tr>
                    <th>العضو</th>
                    {AGENCY_KPI_DEFS.map((d) => (
                      <th key={d.key} className="col-num" title={d.description}>
                        {d.label}
                      </th>
                    ))}
                    <th className="col-num">النقاط</th>
                    <th>التقدير</th>
                    <th className="col-act" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr
                      key={m.employeeId}
                      className="clickable"
                      onClick={() => navigate(`/agency/team/${m.employeeId}`)}
                    >
                      <td>
                        <span className="row" style={{ gap: 8, alignItems: 'center' }}>
                          <Avatar name={m.name} src={m.avatarUrl} size={28} />
                          <span>
                            <div className="cell-strong">{m.name}</div>
                            <div className="tiny mute">
                              {m.jobTitle ?? '—'}
                              {m.deptLabel ? ` · ${m.deptLabel}` : ''}
                            </div>
                          </span>
                        </span>
                      </td>

                      {AGENCY_KPI_DEFS.map((d) => (
                        <td key={d.key} className="num cell-mute">
                          <KpiCell value={m.kpis.find((k) => k.key === d.key)} kpi={d.key} />
                        </td>
                      ))}

                      <td className="num cell-strong">
                        {m.score == null ? (
                          '—'
                        ) : (
                          <span className="row" style={{ gap: 5, justifyContent: 'flex-end' }}>
                            {m.score}
                            {m.trend != null && m.trend !== 0 && (
                              <span
                                className={`tiny num${m.trend > 0 ? ' ag-up' : ' ag-late-text'}`}
                                title="عن الفترة السابقة"
                              >
                                {m.trend > 0 ? `+${m.trend}` : m.trend}
                              </span>
                            )}
                          </span>
                        )}
                      </td>

                      <td>
                        {m.grade ? (
                          <Badge kind={KPI_GRADE_DEFS.find((g) => g.key === m.grade)?.kind}>
                            {KPI_GRADE_DEFS.find((g) => g.key === m.grade)?.label}
                          </Badge>
                        ) : (
                          <span className="tiny mute">{m.stateReason ?? 'لا قياس'}</span>
                        )}
                      </td>

                      <td className="col-act">
                        <Icon name="chevronLeft" size={14} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rows.length === 0 && (
              <p className="mute small" style={{ padding: 'var(--sp-4)' }}>
                {all.length === 0
                  ? 'لا أحدَ في الأقسام التنفيذية بعد — اختر قسمًا تنفيذيًّا في ملفّ الموظّف بالموارد البشرية.'
                  : 'لا أحدَ يطابق البحث.'}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** خليّةُ بندٍ في الجدول — الرقمُ أو سببُ غيابه، ولا صفرَ مكان الغائب. */
function KpiCell({ value, kpi }: { value: KpiValue | undefined; kpi: AgencyKpiKey }) {
  const def = AGENCY_KPI_MAP[kpi];
  if (!value || value.value == null) {
    return <span className="tiny mute">{value?.reason ?? '—'}</span>;
  }
  const shown = def.unit === 'PERCENT' ? `${value.value}٪` : `${value.value}`;
  const good =
    def.direction === 'HIGHER_BETTER' ? value.value >= def.target : value.value <= def.target;
  return (
    <span title={`${value.n} فرصة`}>
      <b className={value.counted ? (good ? 'ag-up' : 'ag-late-text') : 'mute'}>{shown}</b>
      {!value.counted && <span className="tiny mute"> (لا يُحتسب)</span>}
    </span>
  );
}

/**
 * كيف تُقاس — منشورةٌ لكلّ مقيس.
 *
 * رقمٌ لا يستطيع صاحبُه فحصَ مدخلاته لا يُدافَع عنه في أيّ محفل. فتُعرض
 * الصيغةُ والهدفُ وحيلةُ اللعب وحارسُها بلا مواربة.
 */
function HowMeasured({ onClose }: { onClose: () => void }) {
  return (
    <section className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="info" size={15} /> كيف تُقاس هذه الأرقام
        </h2>
        <button className="btn btn-sm" onClick={onClose}>
          إغلاق
        </button>
      </div>
      <div className="card-body">
        <p className="tiny mute mb-4">
          الأرقامُ محسوبةٌ من المهامّ والمراجعات لحظةَ فتح الشاشة — لا تُكتب بيدٍ ولا تُخزَّن.
          والمضبوطُ باليد شيئان: أهدافُ البنود وأوزانُها. وبلوغُ كلّ الأهداف بالضبط يعطي ٨٠
          نقطة لا ١٠٠ — فيبقى فوقها مجالٌ حقيقيّ.
        </p>

        {AGENCY_KPI_DEFS.map((d) => (
          <div key={d.key} className="kpi-explain">
            <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
              <strong>{d.label}</strong>
              <span className="ag-chip num">وزن {d.weight}٪</span>
              <span className="tiny mute num">
                الهدف {d.unit === 'PERCENT' ? `${d.target}٪` : d.target}
                {' · '}
                لا يُحتسب دون {d.nMin} فرص
              </span>
            </div>
            <p className="tiny">{d.description}</p>
            <p className="tiny mute">
              <b>كيف يُلعب:</b> {d.gaming}
            </p>
            <p className="tiny mute">
              <b>وما يحرسه:</b> {d.guard}
            </p>
          </div>
        ))}

        <Notice kind="info" icon="alert" title="وما لا يدخل القياس عمدًا">
          عددُ المهامّ المنجَزة (يكافئ تشظيةَ التسليم إلى بطاقاتٍ صغيرة)، ووحداتُ الخدمة
          (يحرّرها المنفّذُ نفسُه من بطاقته)، ودقائقُ التأخّر في الحضور (لا تقيس قيمةً سُلّمت)،
          وأرقامُ لقطات المراقبة (تقيس الامتثالَ للمراقب لا العمل). وكلٌّ منها بدا معقولًا حتى
          سُئل: كيف يُلعَب؟
        </Notice>
      </div>
    </section>
  );
}
